import type { OrderCountBucket } from '../../orders/order.model';

export type StatisticsMetric = keyof Pick<OrderCountBucket, 'count' | 'revenue' | 'photos'>;

interface MetricDefinition {
  key: StatisticsMetric;
  label: string;
  // Full value, e.g. "12 orders" or "€84.50".
  format: (value: number) => string;
  // Short value for the y-axis, e.g. "1.2K" or "€1.2K".
  formatAxis: (value: number) => string;
}

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
// Same currency settings as the `currency:'EUR':'symbol':'1.2-2':'en-US'` pipe in OrderItem.
const euro = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compactEuro = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', notation: 'compact', maximumFractionDigits: 1 });

const plural = (singular: string, pluralForm: string) => (value: number) =>
  `${decimal.format(value)} ${value === 1 ? singular : pluralForm}`;

export const METRICS = [
  { key: 'count', label: 'Printed orders', format: plural('order', 'orders'), formatAxis: v => compact.format(v) },
  { key: 'revenue', label: 'Revenue', format: v => euro.format(v), formatAxis: v => compactEuro.format(v) },
  { key: 'photos', label: 'Printed photos', format: plural('photo', 'photos'), formatAxis: v => compact.format(v) },
] as const satisfies readonly MetricDefinition[];

export const getMetric = (key: StatisticsMetric): MetricDefinition => METRICS.find(m => m.key === key)!;

// Value shown in a tile: numbers without a unit, revenue as currency.
export const formatMetricValue = (key: StatisticsMetric, value: number): string =>
  key === 'revenue' ? euro.format(value) : integer.format(value);
